/**
 * useReferrerPersona - Detects visitor persona based on HTTP referrer
 *
 * Maps incoming traffic sources to appropriate ViewerProfile:
 * - LinkedIn/Xing → recruiter-view
 * - GitHub → technical-view
 * - Corporate domains → executive-view
 * - Direct/Search → public-view
 */

import { useMemo } from 'react'
import type { ViewerProfile } from '@/types/viewer-profile'

// Known corporate/enterprise domains that suggest executive visitors
const CORPORATE_DOMAINS = [
  'salesforce.com',
  'microsoft.com',
  'google.com',
  'amazon.com',
  'oracle.com',
  'ibm.com',
  'sap.com',
  'accenture.com',
  'deloitte.com',
  'mckinsey.com',
  'bcg.com',
  'bain.com',
]

// Recruiter/talent platforms
const RECRUITER_DOMAINS = [
  'linkedin.com',
  'xing.com',
  'indeed.com',
  'glassdoor.com',
  'hired.com',
  'angel.co',
  'wellfound.com',
]

// Developer/technical platforms
const TECHNICAL_DOMAINS = [
  'github.com',
  'gitlab.com',
  'stackoverflow.com',
  'dev.to',
  'hackernews.com',
  'news.ycombinator.com',
  'reddit.com/r/programming',
  'reddit.com/r/webdev',
  'twitter.com', // Often technical shares
  'x.com',
]

function getDomainFromReferrer(referrer: string): string {
  try {
    const url = new URL(referrer)
    return url.hostname.toLowerCase()
  } catch {
    return ''
  }
}

function matchesDomain(referrer: string, domains: string[]): boolean {
  const hostname = getDomainFromReferrer(referrer)
  const fullReferrer = referrer.toLowerCase()

  return domains.some(domain =>
    hostname.includes(domain) || fullReferrer.includes(domain)
  )
}

export function detectPersonaFromReferrer(referrer: string): ViewerProfile {
  if (!referrer) {
    return 'public-view'
  }

  // Check recruiter platforms first (LinkedIn is common)
  if (matchesDomain(referrer, RECRUITER_DOMAINS)) {
    return 'recruiter-view'
  }

  // Check technical platforms
  if (matchesDomain(referrer, TECHNICAL_DOMAINS)) {
    return 'technical-view'
  }

  // Check corporate domains
  if (matchesDomain(referrer, CORPORATE_DOMAINS)) {
    return 'executive-view'
  }

  // Default for direct traffic, search engines, etc.
  return 'public-view'
}

export interface PersonaContent {
  label: string
  headline: string
  cta: string
}

export const PERSONA_CONTENT: Record<ViewerProfile, PersonaContent> = {
  'recruiter-view': {
    label: '// FOR RECRUITERS & TALENT',
    headline: '30 years bridging Seattle innovation and Swiss precision. CTO, founder, platform architect. Choose how you\'d like to explore my journey.',
    cta: 'Explore Experience',
  },
  'technical-view': {
    label: '// FOR DEVELOPERS & ENGINEERS',
    headline: 'Building systems where 5-person teams do 50-person work. MCP orchestration, edge computing, AI-native architectures. Pick your interface.',
    cta: 'View Architecture',
  },
  'executive-view': {
    label: '// FOR DECISION MAKERS',
    headline: 'Delivering 10x cost reductions as standard. Enterprise infrastructure with startup velocity. Select your view.',
    cta: 'See Results',
  },
  'public-view': {
    label: '// WELCOME',
    headline: 'CTO and digital architect based in Zurich. I build platforms that compound in value. Choose your preferred way to explore.',
    cta: 'Get Started',
  },
}

export function useReferrerPersona(): {
  persona: ViewerProfile
  content: PersonaContent
  referrer: string
} {
  const referrer = typeof document !== 'undefined' ? document.referrer : ''

  const result = useMemo(() => {
    const persona = detectPersonaFromReferrer(referrer)
    const content = PERSONA_CONTENT[persona]

    return { persona, content, referrer }
  }, [referrer])

  return result
}

export default useReferrerPersona
